/**
 * PATH: packages/job-hub/src/jobs/qa/qaHoldPointApi.ts
 * FILE_ID: JOBHUB-QA-HOLD-POINT-API-001
 * PURPOSE: Needs sign-off marker plus request, sign and reject of a check's hold point.
 * WHY: qaInspectionApi.ts is near the 500-line ceiling. Hold (has_hold_point)
 *      parks a check out of Run and the ITP; sign-off keeps it in both.
 */
import type { JobHubHttpClient } from '../jobHubDataAdapter'
import type { QaHoldPoint, QaItemInstance } from './qaTypes'

export interface QaHoldPointSignBody {
  /** QDrive file id of the signature PNG, uploaded by the signer. */
  signatureAssetId: string
  signerName: string
  signerRole?: string
  comment?: string
}

export interface QaHoldPointApi {
  setSignoffRequired(itemInstanceId: string, value: boolean): Promise<QaItemInstance>
  /** Returns the open request when one already exists for the check. */
  requestHoldPoint(body: { areaId: string; itemInstanceId: string; name: string }): Promise<QaHoldPoint>
  approveHoldPoint(holdPointId: string, body: QaHoldPointSignBody): Promise<QaHoldPoint>
  /** The backend refuses a rejection without a comment. */
  rejectHoldPoint(holdPointId: string, comment: string): Promise<QaHoldPoint>
}

export function createQaHoldPointApi(
  client: JobHubHttpClient,
  headers: Record<string, string> | undefined,
): QaHoldPointApi {
  return {
    async setSignoffRequired(itemInstanceId, value) {
      const response = await client.post(
        `/api/qa/item-instances/${itemInstanceId}/set-signoff-required/`,
        { signoff_required: value },
        { headers },
      )
      return response.data as QaItemInstance
    },
    async requestHoldPoint({ areaId, itemInstanceId, name }) {
      const response = await client.post(
        '/api/qa/hold-points/request/',
        { area_id: areaId, item_instance_id: itemInstanceId, name: name.slice(0, 160) },
        { headers },
      )
      return response.data as QaHoldPoint
    },
    async approveHoldPoint(holdPointId, body) {
      const response = await client.post(
        `/api/qa/hold-points/${holdPointId}/approve/`,
        {
          signature: {
            signature_asset_id: body.signatureAssetId,
            signer_name: body.signerName,
            signer_role: body.signerRole ?? '',
          },
          comment: body.comment ?? '',
        },
        { headers },
      )
      return response.data as QaHoldPoint
    },
    async rejectHoldPoint(holdPointId, comment) {
      const response = await client.post(
        `/api/qa/hold-points/${holdPointId}/reject/`,
        { comment },
        { headers },
      )
      return response.data as QaHoldPoint
    },
  }
}
